import { useTranslation } from '@jq4race/i18n';
import type { PlatformSuperAdminListItem } from '@jq4race/shared';
import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@jq4race/ui';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatDateTime } from '../../../../shared/format/format-date';
import { Pagination } from './Pagination';

type SortKey = 'email' | 'status' | 'lastLogin' | 'createdAt';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 10;

interface PlatformAdminsTableProps {
  members: PlatformSuperAdminListItem[];
  loading?: boolean;
  currentUserId: string | null;
  onToggleActive: (member: PlatformSuperAdminListItem) => void;
}

// Members table for the platform team (super_admins). The current user's own
// row never shows a toggle action — a super_admin cannot deactivate/reactivate
// themselves (enforced server-side too, see SetPlatformAdminActiveUseCase).
export function PlatformAdminsTable({
  members,
  loading = false,
  currentUserId,
  onToggleActive,
}: PlatformAdminsTableProps) {
  const { t, i18n } = useTranslation('users');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('email');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter((m) => (m.email ?? '').toLowerCase().includes(term));
  }, [members, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'email':
          return (a.email ?? '').localeCompare(b.email ?? '') * dir;
        case 'status':
          return ((a.active ? 0 : 1) - (b.active ? 0 : 1)) * dir;
        case 'lastLogin': {
          const av = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
          const bv = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
          return (av - bv) * dir;
        }
        case 'createdAt':
          return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
        default:
          return 0;
      }
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sortFor = (key: SortKey): SortDir | null => (sortKey === key ? sortDir : null);

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm flex-1">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-jq-stone"
          strokeWidth={1.5}
        />
        <Input
          type="search"
          placeholder={t('platformAdminsTable.searchPlaceholder')}
          className="h-9 pl-9 text-[13px]"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              sortable
              sortDirection={sortFor('email')}
              onClick={() => toggleSort('email')}
            >
              {t('platformAdminsTable.columns.email')}
            </TableHead>
            <TableHead
              sortable
              sortDirection={sortFor('status')}
              onClick={() => toggleSort('status')}
            >
              {t('platformAdminsTable.columns.status')}
            </TableHead>
            <TableHead
              sortable
              sortDirection={sortFor('lastLogin')}
              onClick={() => toggleSort('lastLogin')}
            >
              {t('platformAdminsTable.columns.lastLogin')}
            </TableHead>
            <TableHead
              sortable
              sortDirection={sortFor('createdAt')}
              onClick={() => toggleSort('createdAt')}
            >
              {t('platformAdminsTable.columns.created')}
            </TableHead>
            <TableHead align="right">&nbsp;</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && members.length === 0 ? (
            <TableSkeleton
              rows={5}
              columns={5}
              cellWidths={[
                'w-[200px]',
                'w-[72px] rounded-full',
                'w-[140px]',
                'w-[140px]',
                'ml-auto w-[80px]',
              ]}
            />
          ) : paged.length === 0 ? (
            <TableEmpty
              colSpan={5}
              message={
                members.length === 0
                  ? t('platformAdminsTable.empty')
                  : t('platformAdminsTable.emptyFiltered')
              }
            />
          ) : (
            paged.map((m) => {
              const isSelf = m.id === currentUserId;
              return (
                <TableRow key={m.id}>
                  <TableCell>
                    <span className="font-medium text-jq-charcoal">{m.email ?? '—'}</span>
                    {isSelf && (
                      <span className="ml-2 align-middle text-[11px] uppercase tracking-widest text-jq-stone">
                        {t('membersTable.you')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={m.active ? 'success' : 'danger'}>
                      {m.active
                        ? t('platformAdminsTable.active')
                        : t('platformAdminsTable.deactivated')}
                    </Badge>
                  </TableCell>
                  <TableCell muted>{formatDateTime(m.lastLoginAt, i18n.language)}</TableCell>
                  <TableCell muted>{formatDateTime(m.createdAt, i18n.language)}</TableCell>
                  <TableCell align="right">
                    {!isSelf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onToggleActive(m)}
                        className={
                          m.active
                            ? 'text-jq-danger hover:bg-jq-danger/10 hover:text-jq-danger'
                            : undefined
                        }
                      >
                        {m.active
                          ? t('platformAdminsTable.deactivate')
                          : t('platformAdminsTable.reactivate')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <Pagination
        total={sorted.length}
        page={safePage}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
